import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { type ReactNode, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { globalDefinitions } from '@/defined-translations'
import { useUpdateContentItem } from '@/modules/content/hooks/useContent'
import { contentTypeTranslations } from '@/modules/content/types/translations'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useToast } from '@/modules/shared/hooks/useToast'
import { RelatedTriggers } from '@/modules/timeline/components/related-triggers/RelatedTriggers'
import { openUrl } from '@/utils/external-resources'
import { useTaskStatusMetaData } from '../../hooks/useTaskStatusMetaData'
import { type ContentTask, TaskStatus } from '../../types/task.types'
import { TaskDetailLayout } from '../detail-layout/TaskDetailLayout'

import type { MessageDescriptor } from '@lingui/core'
import type { Content } from '@/modules/content/types/content.type'

type Props = {
  task: ContentTask
}

export const contentVariants: Record<
  Content['type'],
  {
    icon: ReactNode
    label: MessageDescriptor
  }
> = {
  audio: {
    icon: <FontAwesomeIcon icon={['fal', 'podcast']} />,
    label: contentTypeTranslations.audio,
  },
  video: {
    icon: <FontAwesomeIcon icon={['fal', 'video']} />,
    label: contentTypeTranslations.video,
  },
  article: {
    icon: <FontAwesomeIcon icon={['fal', 'file-lines']} />,
    label: contentTypeTranslations.article,
  },
}

export const ContentDetail = ({ task }: Props) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const { showErrorToast } = useToast()
  const navigate = useNavigate()

  const {
    markContentItemAsRead: { error: updateContentItemError, execute: markContentItemAsRead },
  } = useUpdateContentItem()

  const { daysTillExpiry } = useTaskStatusMetaData(task)

  // Methods
  // ------------------------------------------------------------------------- /
  const handleCtaPress = async () => {
    if (!task.contentItem || !task.careplanActivityId) {
      showErrorToast({
        message: i18n._(
          t({
            id: 'content-detail.toast.not-available',
            comment: 'Content detail toast error message',
            message: 'Deze inhoud is niet langer beschikbaar',
          }),
        ),
      })
      return
    }

    // Mark as read after navigation to avoid interference with navigation
    if (task.status === TaskStatus.Pending) {
      await markContentItemAsRead(task.careplanActivityId)
    }

    // Navigate first to prevent race conditions with state updates
    switch (task.contentItem.type) {
      case 'article': {
        const linkedUrl = task.contentItem?.linkedEntry?.[0]?.url
        if (linkedUrl) {
          openUrl(linkedUrl)
        } else {
          navigate(`/content/article/${task.contentItem.id}`)
        }
        break
      }
      case 'audio': {
        navigate(`/content/audio/${task.contentItem.id}`)
        break
      }
      case 'video': {
        navigate(`/content/video/${task.contentItem.id}`)
        break
      }
    }
  }

  const getStatusDisplay = (): string => {
    if (task.status === TaskStatus.Expired) {
      return i18n._(globalDefinitions.task.expired)
    }

    // contentItem can be null at runtime when the content is no longer
    // available; fall back to a generic label instead of crashing.
    if (!task.contentItem) {
      return i18n._(globalDefinitions.task.no_expiry, {
        date: formatLocale(task.createdAt, 'dd/MM/yyyy'),
      })
    }

    if (task.status === TaskStatus.Completed) {
      return i18n._(globalDefinitions.task.Content[task.contentItem.type].completed, {
        date: formatLocale(task.updatedAt, 'dd/MM/yyyy'),
      })
    }

    if (task.contentItem.type === 'article' || task.contentItem.type === 'video' || task.contentItem.type === 'audio') {
      if (daysTillExpiry === 0) {
        return i18n._(globalDefinitions.task.Content[task.contentItem.type].expires_today)
      }

      if (daysTillExpiry === 1) {
        return i18n._(globalDefinitions.task.Content[task.contentItem.type].expires_in_one_day)
      }

      if (daysTillExpiry && daysTillExpiry > 1) {
        return i18n._(globalDefinitions.task.Content[task.contentItem.type].expires_in_x_days, {
          count: daysTillExpiry,
        })
      }

      return i18n._(globalDefinitions.task.Content[task.contentItem.type].other, {
        date: formatLocale(task.createdAt, 'dd/MM/yyyy'),
      })
    }

    return i18n._(globalDefinitions.task.no_expiry, {
      date: formatLocale(task.createdAt, 'dd/MM/yyyy'),
    })
  }

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (updateContentItemError && showErrorToast) {
      showErrorToast({
        message: i18n._(
          t({
            id: 'content-update.toast.error',
            comment: 'Content item toast error message',
            message: 'Er is een fout opgetreden bij het bijwerken van de status van het content item',
          }),
        ),
      })
    }
  }, [updateContentItemError, showErrorToast])

  // Render
  // ------------------------------------------------------------------------- /
  return (
    <TaskDetailLayout>
      <TaskDetailLayout.Header task={task} statusLabel={getStatusDisplay()} />
      <TaskDetailLayout.Content>
        <FlexColumn gap={theme.UI.Spacing.Space4}>
          {/* DESCRIPTION */}
          {task?.message ? (
            <Markdown content={task.message} typography='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest} />
          ) : null}

          {/* Featured image of article */}
          <StFeaturedImageContainer>
            {task.contentItem?.featuredImage?.url ? (
              <StFeaturedImage src={task.contentItem.featuredImage.url} alt={task.contentItem.title} />
            ) : null}

            {/* Badge container which shows some meta data about content */}
            <StBadgeContainer>
              {/* Type of the content */}
              {task.contentItem ? (
                <StBadge>
                  {contentVariants[task.contentItem.type].icon}
                  {i18n._(contentVariants[task.contentItem.type].label)?.toUpperCase()}
                </StBadge>
              ) : null}

              {/* Duration of content */}
              {task.contentItem?.duration ? <StBadge>{`${task.contentItem?.duration} MIN`}</StBadge> : null}
            </StBadgeContainer>
          </StFeaturedImageContainer>

          {/* Related activities */}
          {task?.trigger && <RelatedTriggers trigger={task.trigger} />}
        </FlexColumn>
      </TaskDetailLayout.Content>

      <TaskDetailLayout.Footer>
        <Button variant='Primary' onClick={handleCtaPress}>
          {task.contentItem?.type === 'article'
            ? t({
                id: 'content-detail.read-article',
                comment: 'Primary button on content detail for articles',
                message: 'Lees artikel',
              })
            : task.contentItem?.type === 'audio'
              ? t({
                  id: 'content-detail.listen-audio',
                  comment: 'Primary button on content detail for audio',
                  message: 'Beluister podcast',
                })
              : task.contentItem?.type === 'video'
                ? t({
                    id: 'content-detail.watch-video',
                    comment: 'Primary button on content detail for video',
                    message: 'Bekijk video',
                  })
                : t({
                    id: 'content-detail.start-task',
                    comment: 'Primary button on content detail for tasks',
                    message: 'Taak starten',
                  })}
        </Button>
      </TaskDetailLayout.Footer>
    </TaskDetailLayout>
  )
}
const StFeaturedImageContainer = styled.div`
  position: relative;
  width: 100%;
`

const StFeaturedImage = styled.img`
  ${({ theme }) => `
    width: 100%;
    height: auto;
    margin-bottom: ${theme.UI.SpacingPx.Space4};
    border-radius: ${theme.UI.BorderRadius.Large}px;
    aspect-ratio: 3 / 2;
    object-fit: cover;
  `}
`

const StBadgeContainer = styled(FlexRow)`
  position: absolute;
  left: 0;
  bottom: ${({ theme }) => theme.UI.SpacingPx.Space5};
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space2} ${theme.UI.SpacingPx.Space3}`};

  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StBadge = styled(FlexRow)`
  border-radius: 5px;
  background: rgba(12, 22, 56, 0.4);
  backdrop-filter: blur(2px);

  color: ${({ theme }) => theme.Colors.Neutrals.Lightest};
  font-size: ${({ theme }) => theme.Typography.BodySmallBold};
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space1} ${theme.UI.SpacingPx.Space2}`};

  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
  align-items: center;
`
